import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { fetchAds } from '../../utils/api';
import { formatLocalDateTime } from '../../utils/dateTime';

const STATUS_LABELS = {
  0: 'Pending',
  1: 'Approved',
  2: 'Expired',
  3: 'Rejected',
};
const getStatusColor = (status) => {
  if (status === 1) return 'success';
  if (status === 3) return 'error';
  if (status === 2) return 'default';
  return 'warning';
};

const MyAds = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetchAds();
        if (cancelled) return;
        const data = Array.isArray(res?.data) ? res.data : [];
        setAds(data);
      } catch (err) {
        console.error('Error fetching ads:', err);
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load ads.');
          setAds([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const sliced = ads.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        My Ads
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      <Paper elevation={2}>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Business</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Expiry</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sliced.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No ads yet. Create your first ad from the Create Ad page.
                  </TableCell>
                </TableRow>
              ) : (
                sliced.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.title || '-'}</TableCell>
                    <TableCell>{row.businessTitle || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={STATUS_LABELS[row.status] ?? row.status}
                        color={getStatusColor(row.status)}
                      />
                    </TableCell>
                    <TableCell>{formatLocalDateTime(row.expiryDate)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={ads.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default MyAds;
